import React, { useContext, useState } from 'react';
import { FuelButton, FuelTag, FuelTypography } from '@frete.com/fuel-react';
import { FuelFlex } from '@frete.com/fuel-station-react';
import AppContext from '@context';
import Pulse, { PulsePropsInterface } from '@components/ui/PulseV2';
import { formatDateToPatternRequested } from '@utils/formatters';
import { occurrenceRead } from '@services/journeys';
import { ItemPropsInterface } from './ItemTypes';
import * as Styled from './ItemStyles';

function Item({
  id,
  type,
  date,
  title,
  description,
  tag,
  isFirstPrint,
  isLastPrint,
  read,
  journeyId,
  meta,
}: ItemPropsInterface) {
  const [isLoadingViewedOccurence, setILoadingViewedOccurence] =
    useState(false);
  const { dispatch } = useContext(AppContext);
  const pulseProps: PulsePropsInterface['icon'] = {
    align: 'center',
    isPulse: false,
    color: 'gray',
    size: 'sm',
  };

  if (isLastPrint && type === 'locationCity') {
    pulseProps.isPulse = true;
    pulseProps.size = 'md';
    pulseProps.color = 'green';
  }

  if (type === 'occurrence') {
    pulseProps.name = 'alert';
    pulseProps.style = 'filled';

    if (!read) {
      pulseProps.color = 'orange';
    }
  }

  if (type === 'cargo') {
    pulseProps.name = 'one-check';
  }

  function handleViewedOccurence() {
    if (!isLoadingViewedOccurence) {
      setILoadingViewedOccurence(true);

      occurrenceRead(id)
        .then(() => {
          dispatch({
            type: 'journey_occurrence_read',
            payload: { read: true, occurrenceId: id, journeyId },
          });
        })
        .catch(() => {
          window.dispatchEvent(
            new CustomEvent('notificationShow', {
              detail: {
                heading: 'Não foi possível realizar esta ação',
                message: 'Tente novamente mais tarde',
                severity: 'error',
              },
            }),
          );
        })
        .finally(() => {
          setILoadingViewedOccurence(false);
        });
    }
  }

  function handleRenderTagOrAction() {
    if (type === 'occurrence' && !read) {
      return (
        <FuelFlex
          defaultProps={{
            width: '117px',
          }}
        >
          <FuelButton
            variant="secondary"
            size="small"
            fullWidth
            isLoading={isLoadingViewedOccurence}
            onClick={() => handleViewedOccurence()}
          >
            Resolvido
          </FuelButton>
        </FuelFlex>
      );
    }

    if (tag.label) {
      return (
        <FuelFlex>
          <FuelTag label={tag.label} uppercase={false} />
        </FuelFlex>
      );
    }

    return null;
  }

  return (
    <FuelFlex
      defaultProps={{
        gap: 'km4',
        justifyContent: 'space-between',
        customMargin: '8px 0 0 0',
        height: '54px',
      }}
      testId="history-item"
    >
      <FuelFlex defaultProps={{ gap: 'km4' }}>
        <FuelFlex
          defaultProps={{
            gap: 'km1',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <FuelTypography
            color="neutral-default"
            size="overline"
            weight="bold"
            style={{ textTransform: 'uppercase' }}
            spacingTop={4}
          >
            {formatDateToPatternRequested(date, 'dd MMM yyyy')}
          </FuelTypography>
          <FuelTypography color="neutral-default" size="overline" weight="bold">
            {formatDateToPatternRequested(date, 'HH:mm')}
          </FuelTypography>
        </FuelFlex>
        <Styled.WrapperLine isFirstPrint={isFirstPrint}>
          <Pulse icon={pulseProps} />
        </Styled.WrapperLine>
        <FuelFlex
          defaultProps={{
            gap: 'km1',
            flexDirection: 'column',
          }}
        >
          <FuelTypography
            size="body-sm"
            weight={isLastPrint ? 'bold' : 'medium'}
          >
            {title}
          </FuelTypography>

          {description && (
            <FuelTypography
              size="caption"
              color="neutral-default"
              weight="medium"
            >
              {description}
            </FuelTypography>
          )}

          <FuelTypography
            size="caption"
            color="neutral-default"
            weight="medium"
            data-testid="in-geofence-description"
          >
            {meta?.in_geofence_description}
          </FuelTypography>
        </FuelFlex>
      </FuelFlex>
      {handleRenderTagOrAction()}
    </FuelFlex>
  );
}

export default Item;
